import React from 'react';
import { buildShareUrl, normaliseCustomDomain } from '../utils/shareLinks';
import { DEFAULT_RENDER_FONT, RENDER_FONT_OPTIONS } from '../utils/renderFonts';

const linkCategories = [
  'album',
  'product',
  'store',
  'catalogue',
  'service',
  'listing',
  'register',
  'document',
  'profile',
  'event',
  'menu',
  'invite',
];

const currencyOptions = ['ZAR', 'USD', 'EUR', 'GBP'];
const isValidHexColor = (value = '') => /^#(?:[0-9a-f]{3}){1,2}$/i.test(value.trim());

function LinkMetadataForm({ link, onChange }) {
  const updateField = (field, value) => {
    onChange({ ...link, [field]: value });
  };

  const shareUrl = buildShareUrl(link);
  const hasLinkPrice = ['product', 'service'].includes(link.category);

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-5">
      <div className="mb-5 flex items-start justify-between gap-4">
        <div>
          <p className="label-caps">Asset link creation</p>
          <h1 className="mt-2 text-2xl font-semibold leading-tight tracking-normal">
            Package assets into a branded link.
          </h1>
        </div>
        <span className="mt-1 h-2 w-2 rounded-full bg-blue-500" aria-hidden="true" />
      </div>

      <label className="field-label" htmlFor="title">Title</label>
      <input
        id="title"
        className="field"
        value={link.title}
        onChange={(event) => updateField('title', event.target.value)}
      />

      <label className="field-label" htmlFor="category">Link category</label>
      <select
        id="category"
        className="field"
        value={link.category || 'album'}
        onChange={(event) => updateField('category', event.target.value)}
      >
        {linkCategories.map((category) => (
          <option key={category} value={category}>
            {category.charAt(0).toUpperCase() + category.slice(1)}
          </option>
        ))}
      </select>

      <label className="field-label" htmlFor="render-font">Font type</label>
      <select
        id="render-font"
        className="field"
        value={link.renderFont || DEFAULT_RENDER_FONT}
        onChange={(event) => updateField('renderFont', event.target.value)}
      >
        {RENDER_FONT_OPTIONS.map((fontOption) => (
          <option key={fontOption.value} value={fontOption.value}>
            {fontOption.label}
          </option>
        ))}
      </select>

      <label className="field-label" htmlFor="background-color">Background color</label>
      <div className="grid grid-cols-[1fr_3rem] gap-2">
        <input
          id="background-color"
          className="field"
          inputMode="text"
          maxLength="7"
          pattern="#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})"
          placeholder="#f7f9fb"
          value={link.backgroundColor || ''}
          onChange={(event) => updateField('backgroundColor', event.target.value)}
        />
        <span
          aria-label="Selected background color"
          className="mt-2 block h-12 rounded border border-slate-200"
          role="img"
          style={{ backgroundColor: isValidHexColor(link.backgroundColor) ? link.backgroundColor.trim() : '#f7f9fb' }}
        />
      </div>

      <label className="field-label" htmlFor="header-background-color">Header background color</label>
      <div className="grid grid-cols-[1fr_3rem] gap-2">
        <input
          id="header-background-color"
          className="field"
          inputMode="text"
          maxLength="7"
          pattern="#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})"
          placeholder="#ffffff"
          value={link.headerBackgroundColor || ''}
          onChange={(event) => updateField('headerBackgroundColor', event.target.value)}
        />
        <span
          aria-label="Selected header background color"
          className="mt-2 block h-12 rounded border border-slate-200"
          role="img"
          style={{ backgroundColor: isValidHexColor(link.headerBackgroundColor) ? link.headerBackgroundColor.trim() : '#ffffff' }}
        />
      </div>

      <label className="mt-4 flex items-center gap-2 text-sm font-semibold text-slate-700" htmlFor="show-create-link-button">
        <input
          id="show-create-link-button"
          type="checkbox"
          checked={link.showCreateLinkButton !== false}
          onChange={(event) => updateField('showCreateLinkButton', event.target.checked)}
        />
        Show Create Link button in the public header
      </label>

      <label className="field-label" htmlFor="asset-card-background-color">Asset card background color</label>
      <div className="grid grid-cols-[1fr_3rem] gap-2">
        <input
          id="asset-card-background-color"
          className="field"
          inputMode="text"
          maxLength="7"
          pattern="#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})"
          placeholder="#ffffff"
          value={link.assetCardBackgroundColor || ''}
          onChange={(event) => updateField('assetCardBackgroundColor', event.target.value)}
        />
        <span
          aria-label="Selected asset card background color"
          className="mt-2 block h-12 rounded border border-slate-200"
          role="img"
          style={{ backgroundColor: isValidHexColor(link.assetCardBackgroundColor) ? link.assetCardBackgroundColor.trim() : '#ffffff' }}
        />
      </div>

      {hasLinkPrice && (
        <div className="grid grid-cols-[5.5rem_1fr] gap-2">
          <div>
            <label className="field-label" htmlFor="currency">Currency</label>
            <select
              id="currency"
              className="field"
              value={link.currency || 'ZAR'}
              onChange={(event) => updateField('currency', event.target.value)}
            >
              {currencyOptions.map((currency) => (
                <option key={currency} value={currency}>{currency}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="field-label" htmlFor="price">Price</label>
            <input
              id="price"
              className="field"
              inputMode="decimal"
              placeholder="0.00"
              value={link.price || ''}
              onChange={(event) => updateField('price', event.target.value)}
            />
          </div>
        </div>
      )}

      <label className="field-label" htmlFor="description">Description</label>
      <textarea
        id="description"
        className="field min-h-24 resize-none"
        value={link.description}
        onChange={(event) => updateField('description', event.target.value)}
      />

      <div className="mt-4 border-t border-slate-200 pt-4">
        <p className="text-sm font-semibold text-slate-950">Contact details</p>
        <div className="grid gap-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="field-label" htmlFor="contact-tel">Telephone</label>
              <input
                id="contact-tel"
                className="field"
                placeholder="+27 82 123 4567"
                type="tel"
                value={link.contactTel || ''}
                onChange={(event) => updateField('contactTel', event.target.value)}
              />
            </div>
            <div>
              <label className="field-label" htmlFor="contact-whatsapp">WhatsApp</label>
              <input
                id="contact-whatsapp"
                className="field"
                placeholder="+27 82 123 4567"
                type="tel"
                value={link.contactWhatsApp || ''}
                onChange={(event) => updateField('contactWhatsApp', event.target.value)}
              />
            </div>
          </div>
          <div>
            <label className="field-label" htmlFor="contact-email">Email</label>
            <input
              id="contact-email"
              className="field"
              placeholder="hello@example.com"
              type="email"
              value={link.contactEmail || ''}
              onChange={(event) => updateField('contactEmail', event.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="field-label" htmlFor="owner">Owner</label>
          <input
            id="owner"
            className="field"
            value={link.owner}
            onChange={(event) => updateField('owner', event.target.value)}
          />
        </div>
        <div>
          <label className="field-label" htmlFor="slug">Slug</label>
          <input
            id="slug"
            className="field"
            value={link.slug}
            onChange={(event) => updateField('slug', event.target.value.toLowerCase().replace(/\s+/g, '-'))}
          />
        </div>
      </div>

      <label className="mt-4 flex items-center gap-2 text-sm font-semibold text-slate-700" htmlFor="use-custom-domain">
        <input
          id="use-custom-domain"
          type="checkbox"
          checked={Boolean(link.useCustomDomain)}
          onChange={(event) => {
            onChange({
              ...link,
              useCustomDomain: event.target.checked,
              customDomain: event.target.checked ? link.customDomain || '' : '',
            });
          }}
        />
        Use custom domain for this share link
      </label>

      {link.useCustomDomain && (
        <>
          <label className="field-label" htmlFor="custom-domain">Custom domain</label>
          <input
            id="custom-domain"
            className="field"
            placeholder="share.example.com"
            value={link.customDomain || ''}
            onChange={(event) => updateField('customDomain', normaliseCustomDomain(event.target.value))}
          />
        </>
      )}

      <div className="mt-4 rounded border border-slate-200 bg-slate-50 p-3">
        <p className="text-xs text-slate-500">Public share URL</p>
        <p className="mt-1 break-all text-sm font-semibold text-slate-950">{shareUrl}</p>
      </div>

    </section>
  );
}

export default LinkMetadataForm;
